// Static Points Guides — short, SEO-friendly educational articles.
// Content is editorial/informational, not financial advice.

export const GUIDES = [
  {
    slug: "what-is-one-point-worth",
    title: "What is 1 point worth?",
    dek: "The honest answer: usually between 1 and 2 cents — but it depends entirely on how you redeem.",
    category: "Basics",
    readTime: "3 min",
    updated: "June 2026",
    accent: "blue",
    sections: [
      {
        heading: "The short answer",
        body: [
          "Most major credit-card points are worth somewhere between 1 and 2 cents each. That sounds small, but on a 60,000-point welcome bonus it's the difference between $600 and $1,200 — real money.",
          "The exact value isn't fixed. It depends on the program (Chase, Amex, Capital One, etc.) and, more importantly, on how you choose to cash them in.",
        ],
      },
      {
        heading: "The floor: cash back at ~1 cent",
        body: [
          "Almost every points currency lets you redeem for cash back or a statement credit at roughly 1 cent per point. Think of this as the floor — the least your points should ever be worth.",
          "If a redemption gives you less than 1 cent per point (some gift cards and merchandise do), it's usually a bad deal.",
        ],
      },
      {
        heading: "The ceiling: transfers to travel partners",
        body: [
          "The highest values come from transferring points to airline and hotel partners and booking premium flights or hotel nights. Here, smart redemptions can hit 2 cents per point or more.",
          "This takes a little effort and flexibility, but it's where the outsized value lives.",
        ],
      },
      {
        heading: "How to estimate your own number",
        body: [
          "The math is simple: value = points × cents-per-point ÷ 100. So 50,000 points at 1.5 cents = $750.",
          "Use our calculator on the homepage to see a realistic range for your program instantly.",
        ],
      },
    ],
    takeaway:
      "For planning, assume ~1.25 cents per point for major bank programs — and more if you're willing to redeem for travel.",
  },
  {
    slug: "points-or-cash",
    title: "Should you use points or pay cash?",
    dek: "A one-sentence rule that tells you when to spend points and when to keep them.",
    category: "Strategy",
    readTime: "3 min",
    updated: "June 2026",
    accent: "emerald",
    sections: [
      {
        heading: "The 1-cent rule",
        body: [
          "Here's the shortcut: work out how many cents per point a redemption gives you. If it's clearly above 1 cent, using points is a good deal. If it's around or below 1 cent, pay cash and keep your points for something better.",
          "Example: a $500 flight that costs 30,000 points = 1.67 cents per point. That's a strong use. A $50 gift card for 6,000 points = 0.83 cents — skip it.",
        ],
      },
      {
        heading: "When cash usually wins",
        body: [
          "Small redemptions, gift cards, merchandise, and statement credits tend to give you a penny or less per point. In those cases, pay with cash and let your points keep their optionality.",
          "Cash is also the right call when you'd otherwise stretch to 'afford' something just because you have points.",
        ],
      },
      {
        heading: "When points usually win",
        body: [
          "Award flights, hotel nights, and transfers to travel partners are where points routinely beat cash — often by a wide margin, especially for premium cabins and peak-season hotels.",
        ],
      },
      {
        heading: "Don't hoard forever",
        body: [
          "Points are a currency that tends to lose value over time as programs devalue. Earning and burning in a reasonable window usually beats saving for a 'someday' redemption that keeps getting more expensive.",
        ],
      },
    ],
    takeaway:
      "Use points when a redemption beats ~1 cent each. Otherwise, pay cash and save your points for high-value travel.",
  },
  {
    slug: "when-to-transfer-points",
    title: "When should you transfer points?",
    dek: "Transfers unlock the biggest value — but they're usually one-way. Here's how to do it right.",
    category: "Strategy",
    readTime: "3 min",
    updated: "June 2026",
    accent: "yellow",
    sections: [
      {
        heading: "Only transfer with a plan",
        body: [
          "Transfers from a bank program (like Chase or Amex) to an airline or hotel are almost always one-way and can't be reversed. Never transfer speculatively 'just in case.'",
          "Confirm the award you want is actually available before you move a single point.",
        ],
      },
      {
        heading: "Watch for transfer bonuses",
        body: [
          "Programs regularly run limited-time transfer bonuses (e.g. 20–30% extra points to a partner). If you were going to transfer anyway, a bonus is free extra value.",
        ],
      },
      {
        heading: "Look for sweet spots",
        body: [
          "The best transfer value tends to come from a handful of 'sweet spot' redemptions — specific routes or hotels where the award price is unusually low relative to the cash price. A little research goes a long way.",
        ],
      },
    ],
    takeaway:
      "Transfer only when you've confirmed a specific, high-value redemption — and grab transfer bonuses when they line up.",
  },
  {
    slug: "cash-back-vs-travel",
    title: "Cash back vs travel: which wins?",
    dek: "Both are valid. The right choice depends on how much effort you want to put in.",
    category: "Basics",
    readTime: "2 min",
    updated: "June 2026",
    accent: "blue",
    sections: [
      {
        heading: "Cash back: simple and flexible",
        body: [
          "Cash back is predictable — roughly 1 cent per point, no strings attached. It's the right choice if you value simplicity, don't travel much, or want maximum flexibility.",
        ],
      },
      {
        heading: "Travel: more value, more effort",
        body: [
          "Redeeming for travel — especially via transfer partners — can push your value to 1.5–2+ cents per point. The trade-off is more research and less flexibility.",
        ],
      },
      {
        heading: "Who should pick what",
        body: [
          "If you'd rather not think about it, earn cash back and move on. If you travel a few times a year and enjoy optimizing, travel redemptions can be worth 50–100% more.",
        ],
      },
    ],
    takeaway:
      "No wrong answer: choose cash back for simplicity, travel for maximum value. Many people do both.",
  },
  {
    slug: "points-mistakes-to-avoid",
    title: "5 points mistakes to avoid",
    dek: "The most common ways people quietly lose value on their rewards.",
    category: "Tips",
    readTime: "3 min",
    updated: "June 2026",
    accent: "emerald",
    sections: [
      {
        heading: "1. Redeeming for gift cards or merchandise",
        body: ["These options usually give you less than a penny per point. Almost always a worse deal than cash back or travel."],
      },
      {
        heading: "2. Hoarding points for years",
        body: ["Programs devalue over time. Points you're saving today may buy less next year. Earn and burn on a reasonable timeline."],
      },
      {
        heading: "3. Ignoring transfer partners",
        body: ["The 'book through the portal' button is convenient, but transfers to airline and hotel partners often deliver far more value."],
      },
      {
        heading: "4. Letting points expire",
        body: ["Some airline and hotel points expire after inactivity. A single small transaction or redemption usually resets the clock."],
      },
      {
        heading: "5. Transferring speculatively",
        body: ["Never move points to a partner before confirming the exact award you want is available. Transfers are typically one-way."],
      },
    ],
    takeaway:
      "Avoid gift cards, don't hoard, learn transfer partners, watch expirations, and never transfer without a plan.",
  },
];

export const getGuide = (slug) => GUIDES.find((g) => g.slug === slug);
